import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDigest } from '../../hooks/useDigest';
import NextFiringStrip from './NextFiringStrip';
import ExecutiveStrip from './ExecutiveStrip';
import AttentionCard from './AttentionCard';
import BurndownWidget from './BurndownWidget';
import ForecastWidget from './ForecastWidget';
import ErrorBoundary from '../common/ErrorBoundary';
import DailyHeartbeatCard from './DailyHeartbeatCard';

interface CommandCenterZoneProps {
  /** Upcoming scheduled events — passed from Home.tsx to avoid duplicate fetch */
  upcomingEvents: any[];
}

const CommandCenterZone: React.FC<CommandCenterZoneProps> = ({ upcomingEvents }) => {
  const { darkMode } = useTheme();

  // Single digest call for all 4 tools — 5-minute auto-refresh
  const { byTool, loading: digestLoading } = useDigest({
    tools: ['summary', 'attention', 'burndown', 'forecast'],
    refreshInterval: 300000,
  });

  return (
    <div className="space-y-3">
      {/* Daily Heartbeat — promoted to top as the primary daily touchpoint */}
      <ErrorBoundary fallback={null}>
        <DailyHeartbeatCard />
      </ErrorBoundary>

      {/* Next Firing Strip */}
      <ErrorBoundary fallback={null}>
        <NextFiringStrip events={upcomingEvents} />
      </ErrorBoundary>

      {/* Executive Strip */}
      <ErrorBoundary fallback={<div className={`rounded-lg border-l-4 border-indigo-500 overflow-hidden text-xs text-center py-3 ${darkMode ? 'bg-gray-800/60 text-gray-600' : 'bg-white text-gray-500'}`}>Executive metrics unavailable.</div>}>
        <ExecutiveStrip data={byTool['admin_work_summary']?.data ?? null} loading={digestLoading} />
      </ErrorBoundary>

      {/* 3-column cards: Attention, Burndown, Forecast */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ErrorBoundary fallback={<div className={`rounded-lg border-l-4 border-rose-500 p-2 text-xs ${darkMode ? 'bg-gray-800/60 text-gray-600' : 'bg-white text-gray-500'}`}>Attention unavailable.</div>}>
          <AttentionCard data={byTool['admin_work_attention']?.data ?? null} loading={digestLoading} />
        </ErrorBoundary>

        <ErrorBoundary fallback={<div className={`rounded-lg border-l-4 border-emerald-500 p-2 text-xs ${darkMode ? 'bg-gray-800/60 text-gray-600' : 'bg-white text-gray-500'}`}>Burndown unavailable.</div>}>
          <BurndownWidget data={byTool['admin_work_burndown']?.data ?? null} loading={digestLoading} />
        </ErrorBoundary>

        <ErrorBoundary fallback={<div className={`rounded-lg border-l-4 border-emerald-500 p-2 text-xs ${darkMode ? 'bg-gray-800/60 text-gray-600' : 'bg-white text-gray-500'}`}>Forecast unavailable.</div>}>
          <ForecastWidget data={byTool['admin_work_forecast']?.data ?? null} loading={digestLoading} />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default CommandCenterZone;
